import { BranchRecord } from '@/apis/branches';
import { getCampaignsByIds } from '@/apis/campaigns';
import { getEventsByIds } from '@/apis/events';
import { EventTypeRecord, getEventTypes } from '@/apis/eventTypes';
import { GeometryType } from '@/apis/geometries';
import { getMailingsByIds } from '@/apis/mailings';
import { getTagsByIds, TagRecord } from '@/apis/tags';
import { AutocompleteResult, AutocompleteResultWithTooltip } from '@/types';
import { DomainCode } from '@australiangreens/ag-internal-components';
import { EventRecordResult } from '@/types/filterCriteriaRecords';
import {
  loadCampaigns,
  loadUsers,
} from '@pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/callingStatus';
import { loadGeometries } from '@pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/geometryIds';
import { loadGroups } from '@pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/groupIds';
import { loadLists } from '@pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/listIdsLoader';
import { loadBranches } from '@pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/membership';
import { migrateRedundantEventTypes } from '@pages/SavedSearchesPage/utils';
import { FilterCriteria } from './filterCriteriaReducer';

export type QuickFilterRelatedData = {
  groups: AutocompleteResult[];
  geometries: { id: number; geometryType: GeometryType; label: string }[];
  branches: BranchRecord[];
  users: { id: string; label: string }[];
  campaigns: AutocompleteResultWithTooltip[];
  eventCampaigns: AutocompleteResult[];
  events: EventRecordResult[];
  eventTypes: EventTypeRecord[];
  lists: { id: number; label: string }[];
  callingStatusLists: { id: number; label: string }[];
  tags: TagRecord[];
  mailings: AutocompleteResultWithTooltip[];
  missingFilters: string[];
};

export async function loadQuickFilterRelatedData({
  filter,
  token,
  domainCode,
  userSub,
}: {
  filter: FilterCriteria;
  token: string;
  domainCode: DomainCode;
  userSub: string;
}): Promise<QuickFilterRelatedData> {
    type GeometryReturnType = {
      id: number;
      geometryType: GeometryType;
      label: string;
    };

    let geometries = [] as GeometryReturnType[];
    let isMissingGeometry = false;
    if (filter.geometries && filter.geometries.length > 0) {
      const loadGeometriesResult = await loadGeometries({
        token,
        domainCode,
        geometryIds: filter.geometries.map(({ id }) => id),
        userSub: userSub,
      });
      geometries = loadGeometriesResult.geometries;
      isMissingGeometry = loadGeometriesResult.isMissing;
    }

    let groups = [] as AutocompleteResult[];
    let isMissingGroup = false;
    if (filter.groups && filter.groups.length > 0) {
      const loadGroupsResult = await loadGroups({
        token,
        domainCode,
        groupIds: filter.groups.map(({ id }) => id),
      });
      groups = loadGroupsResult.groups;
      isMissingGroup = loadGroupsResult.isMissing;
    }
    let branches = [] as BranchRecord[];
    let isMissingBranch = false;
    if (filter.branches && filter.branches.length > 0) {
      const loadBranchesResult = await loadBranches({
        token,
        domainCode,
        branchIds: filter.branches.map(({ id }) => id),
      });
      branches = loadBranchesResult.branches.map((x) => {
        return { ...x, id: x.value };
      });
      isMissingBranch = loadBranchesResult.isMissing;
    }

    type UserReturnType = {
      id: string;
      label: string;
    };

    let users = [] as UserReturnType[];
    let isUserMissing = false;
    if (filter.callingstatusUsers && filter.callingstatusUsers.length > 0) {
      const loadUsersResult = await loadUsers({
        token,
        userSubs: filter.callingstatusUsers.map(({ id }) => String(id)),
      });
      users = loadUsersResult.users;
      isUserMissing = loadUsersResult.isMissing;
    }

    let campaigns = [] as AutocompleteResultWithTooltip[];
    let isMissingCampaign = false;
    if (filter.callingstatusCampaigns && filter.callingstatusCampaigns.length > 0) {
      const loadCampaignsResult = await loadCampaigns({
        token,
        domainCode,
        campaignIds: filter.callingstatusCampaigns.map(({ id }) => id),
      });
      campaigns = loadCampaignsResult.campaigns;
      isMissingCampaign = loadCampaignsResult.isMissing;
    }

    let eventCampaigns = [] as AutocompleteResult[];
    let isMissingEventCampaign = false;
    if (filter.eventCampaigns && filter.eventCampaigns.length > 0) {
      const loadEventCampaignsResult = await getCampaignsByIds(
        token,
        filter.eventCampaigns.map(({ id }) => id)
      );
      const loadEventCampaignsIds = loadEventCampaignsResult.map(({ id }) => id);
      eventCampaigns = filter.eventCampaigns.filter((eventCampaign) =>
        loadEventCampaignsIds.includes(eventCampaign.id)
      );
      isMissingEventCampaign = filter.eventCampaigns.length !== loadEventCampaignsResult.length;
    }

    let events = [] as EventRecordResult[];
    let isMissingEvent = false;
    if (filter.events && filter.events.length > 0) {
      const loadEventsResult = await getEventsByIds(
        token,
        filter.events.map(({ id }) => id),
        domainCode
      );
      const loadEventIds = loadEventsResult.map(({ id }) => id);
      events = filter.events.filter((event) => loadEventIds.includes(event.id));
      isMissingEvent = events.length !== filter.events.length;
    }

    const cleanEventTypes = migrateRedundantEventTypes(filter.eventTypes ?? []);
    let eventTypes = [] as EventTypeRecord[];
    let isMissingEventType = false;
    if (cleanEventTypes.length > 0) {
      const loadEventTypesResult = await getEventTypes(token);
      const loadEventTypeIds = loadEventTypesResult.map(({ id }) => id);
      eventTypes = cleanEventTypes.filter((eventType) => loadEventTypeIds.includes(eventType.id));
      isMissingEventType = eventTypes.length !== cleanEventTypes.length;
    }

    type ListReturnType = {
      id: number;
      label: string;
    };

    let lists = [] as ListReturnType[];
    let isListMissing = false;

    if (filter.lists && filter.lists.length > 0) {
      const loadListsResult = await loadLists({
        token,
        listIds: filter.lists.map(({ id }) => id),
      });
      lists = loadListsResult.lists;
      isListMissing = loadListsResult.isMissing;
    }

    let callingStatusLists = [] as ListReturnType[];
    let iscallingStatusListMissing = false;

    if (filter.callingstatusLists && filter.callingstatusLists.length > 0) {
      const loadCallingStatusListsResult = await loadLists({
        token,
        listIds: filter.callingstatusLists.map(({ id }) => id),
      });
      callingStatusLists = loadCallingStatusListsResult.lists;
      iscallingStatusListMissing = loadCallingStatusListsResult.isMissing;
    }

    let tags = [] as TagRecord[];
    let isTagsMissing = false;

    if (filter.tags && filter.tags.length > 0) {
      tags = await getTagsByIds(
        token,
        filter.tags.map(({ id }) => id),
        domainCode
      );
      isTagsMissing = tags.length < filter.tags.length;
    }

    let mailings = [] as AutocompleteResultWithTooltip[];
    let isMailingsMissing = false;

    if (filter.mailings && filter.mailings.length > 0) {
      const loadMailingsResult = await getMailingsByIds(
        token,
        filter.mailings.map(({ id }) => id)
      );
      const loadMailingsIds = loadMailingsResult.map(({ id }) => id);
      mailings = filter.mailings.filter((mailing) => loadMailingsIds.includes(mailing.id));
      isMailingsMissing = mailings.length < filter.mailings.length;
    }

    const missingFilters = [];

    if (isMissingCampaign) missingFilters.push('Calling status: Campaigns');
    if (iscallingStatusListMissing) missingFilters.push('Calling status: Lists');
    if (isUserMissing) missingFilters.push('Calling status: Users');
    if (isMissingEventCampaign) missingFilters.push('Event attendance: Campaigns');
    if (isMissingEvent) missingFilters.push('Event attendance: Events');
    if (isMissingEventType) missingFilters.push('Event attendance: Event Types');
    if (isMissingGroup) missingFilters.push('Groups');
    if (isListMissing) missingFilters.push('Lists');
    if (isMissingGeometry) missingFilters.push('Location');
    if (isMailingsMissing) missingFilters.push('Mailings');
    if (isMissingBranch) missingFilters.push('Membership status: Branch');
    if (isTagsMissing) missingFilters.push('Tags');

  return {
    groups,
    geometries,
    branches,
    users,
    campaigns,
    eventCampaigns,
    events,
    eventTypes,
    lists,
    callingStatusLists,
    tags,
    mailings,
    missingFilters,
  };
}
